import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Activities.css";
import {
  getTaskList,
  getTaskListEquipmentGroups,
  taskListQueryKey,
} from "../services/taskListApi";

const PAGE_SIZES = [50, 100, 150, 200];
const COLUMN_COUNT = 8;

const projectIdFromStorage = () => {
  try {
    const value = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return String(value?.id || value || "").trim();
  } catch {
    return "";
  }
};

const positiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

function Activities({ isSidebarExpanded }) {
  const location = useLocation();
  const navigate = useNavigate();
  const initial = useMemo(() => new URLSearchParams(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [projectId, setProjectId] = useState(
    () => initial.get("project_id") || projectIdFromStorage(),
  );
  const [activeTaskType, setActiveTaskType] = useState(() =>
    initial.get("task") === "end" ? "end" : "start",
  );
  const [page, setPage] = useState(() => positiveInteger(initial.get("page"), 1));
  const [pageSize, setPageSize] = useState(() => {
    const value = positiveInteger(initial.get("page_size"), 20);
    return PAGE_SIZES.includes(value) ? value : 50;
  });
  const [searchInput, setSearchInput] = useState(() => initial.get("search") || "");
  const [search, setSearch] = useState(() => (initial.get("search") || "").trim());
  const [equipmentGroupId, setEquipmentGroupId] = useState(
    () => initial.get("equipment_group_id") || "",
  );
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [projectTimezone, setProjectTimezone] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [submittingProcessId, setSubmittingProcessId] = useState("");
  const requestSequence = useRef(0);
  const startTabRef = useRef(null);
  const endTabRef = useRef(null);

  const query = useMemo(
    () => ({
      projectId,
      activeTaskType,
      page,
      pageSize,
      search,
      equipmentGroupId,
    }),
    [projectId, activeTaskType, page, pageSize, search, equipmentGroupId],
  );
  const queryKey = useMemo(() => taskListQueryKey(query), [query]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("task", activeTaskType);
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    search ? params.set("search", search) : params.delete("search");
    equipmentGroupId
      ? params.set("equipment_group_id", equipmentGroupId)
      : params.delete("equipment_group_id");
    const next = `${location.pathname}?${params.toString()}`;
    if (`${location.pathname}${location.search}` !== next) {
      navigate(next, { replace: true });
    }
  }, [
    activeTaskType,
    equipmentGroupId,
    location.pathname,
    location.search,
    navigate,
    page,
    pageSize,
    search,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearch((current) => {
        if (current === trimmed) return current;
        setPage(1);
        return trimmed;
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const onProjectChanged = () => {
      setProjectId(projectIdFromStorage());
      setPage(1);
    };
    window.addEventListener("projectChanged", onProjectChanged);
    return () => window.removeEventListener("projectChanged", onProjectChanged);
  }, []);

  useEffect(() => {
    if (!projectId) {
      setGroups([]);
      return undefined;
    }
    const controller = new AbortController();
    getTaskListEquipmentGroups(projectId, controller.signal)
      .then((data) => {
        setGroups(data);
        if (
          equipmentGroupId &&
          !data.some((group) => group.equipment_group_id === equipmentGroupId)
        ) {
          setEquipmentGroupId("");
          setPage(1);
        }
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED") {
          toast.error(error?.response?.data?.message || "Unable to load equipment groups.");
        }
      });
    return () => controller.abort();
    // The selected group is validated only when the project/group catalogue changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadTasks = useCallback(() => {
    if (!projectId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return () => {};
    }

    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    setRows([]);
    setTotal(0);
    setProjectTimezone("");
    setLoading(true);

    getTaskList(query, controller.signal)
      .then((result) => {
        if (sequence !== requestSequence.current) return;
        const nextRows = Array.isArray(result?.data) ? result.data : [];
        setRows(nextRows);
        setTotal(Number(result?.total) || 0);
        setProjectTimezone(
          String(result?.project_tz || nextRows[0]?.project_tz || "").trim(),
        );
        const serverPage = positiveInteger(result?.page, page);
        if (serverPage !== page) setPage(serverPage);
      })
      .catch((error) => {
        if (sequence !== requestSequence.current || error?.code === "ERR_CANCELED") return;
        const message =
          error?.response?.data?.message || "Unable to load the task list.";
        toast.error(
          <span>
            {message}{" "}
            <button
              type="button"
              onClick={() => setRefreshToken((value) => value + 1)}
              style={{ border: 0, background: "none", color: "#2563eb", fontWeight: 700 }}
            >
              Retry
            </button>
          </span>,
          { autoClose: false },
        );
      })
      .finally(() => {
        if (sequence === requestSequence.current) setLoading(false);
      });

    return () => controller.abort();
  }, [page, projectId, query]);

  useEffect(() => loadTasks(), [loadTasks, refreshToken, queryKey]);

  const switchTaskType = (type) => {
    if (type === activeTaskType) return;
    requestSequence.current += 1;
    setRows([]);
    setTotal(0);
    setActiveTaskType(type);
    setPage(1);
  };

  const submitSearchImmediately = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setEquipmentGroupId("");
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const openExistingActionFlow = (row) => {
    if (!row?.equipment_id || submittingProcessId) return;
    setSubmittingProcessId(row.process_id);
    const returnTo = `${location.pathname}${location.search}`;
    navigate(
      `/equipment-details/${row.equipment_id}?process_id=${encodeURIComponent(
        row.process_id,
      )}`,
      {
        state: {
          process_id: row.process_id,
          requestedAction: activeTaskType === "end" ? "End" : "Start",
          taskListReturnUrl: returnTo,
        },
      },
    );
  };

  const formatDate = (value, timeZone) => {
    if (!value || !timeZone) return null;
    const date = new Date(value);
    if (!Number.isFinite(date.getTime()) || date.getUTCFullYear() < 2000) return null;
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
      const time = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date);
      return { date: parts, time };
    } catch {
      return null;
    }
  };

  const renderDate = (value, timeZone) => {
    const formatted = formatDate(value, timeZone);
    if (!formatted) return "—";
    return (
      <span className="task-list-date">
        <span>{formatted.date}</span>
        <span className="task-list-time">{formatted.time}</span>
      </span>
    );
  };

  const handleTabKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const nextType = activeTaskType === "start" ? "end" : "start";
    switchTaskType(nextType);
    window.requestAnimationFrame(() => {
      (nextType === "start" ? startTabRef : endTabRef).current?.focus();
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div
      className={`table-container task-list-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="equipment-page-content">
        <div className="equipment-page-header mt-2">
          <div className="equipment-header-titleblock">
            <h2 className="page-title mb-0">Task List</h2>
          </div>
          <div className="task-list-header-controls">
            <TextField
              className="task-list-search"
              size="small"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={submitSearchImmediately}
              placeholder="Search process, code or equipment"
              inputProps={{ "aria-label": "Search process, code or equipment" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="Clear search"
                      onClick={clearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormControl className="task-list-filter" size="small">
              <Select
                value={equipmentGroupId}
                onChange={(event) => {
                  setEquipmentGroupId(event.target.value);
                  setPage(1);
                }}
                displayEmpty
                inputProps={{ "aria-label": "Equipment Group" }}
              >
                <MenuItem value="">All Assigned Groups</MenuItem>
                {groups.map((group) => (
                  <MenuItem
                    key={group.equipment_group_id}
                    value={group.equipment_group_id}
                  >
                    {group.equipment_group_code
                      ? `${group.equipment_group_code} — ${group.equipment_group_name}`
                      : group.equipment_group_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(searchInput || equipmentGroupId) && (
              <Tooltip title="Clear filters">
                <IconButton
                  className="task-list-clear"
                  size="small"
                  aria-label="Clear all task-list filters"
                  onClick={clearFilters}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton
                className={`task-list-refresh ${loading ? "is-loading" : ""}`}
                size="small"
                aria-label="Refresh task list"
                disabled={loading}
                onClick={() => setRefreshToken((value) => value + 1)}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        {projectTimezone && (
          <div className="task-list-timezone">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            Schedule shown in project timezone: {projectTimezone}
          </div>
        )}
        <div className="task-list-tabs" role="tablist" aria-label="Task type">
          <button
            ref={startTabRef}
            type="button"
            role="tab"
            aria-selected={activeTaskType === "start"}
            tabIndex={activeTaskType === "start" ? 0 : -1}
            className={activeTaskType === "start" ? "start-active" : ""}
            onClick={() => switchTaskType("start")}
            onKeyDown={handleTabKeyDown}
          >
            Ready to Start
          </button>
          <button
            ref={endTabRef}
            type="button"
            role="tab"
            aria-selected={activeTaskType === "end"}
            tabIndex={activeTaskType === "end" ? 0 : -1}
            className={activeTaskType === "end" ? "end-active" : ""}
            onClick={() => switchTaskType("end")}
            onKeyDown={handleTabKeyDown}
          >
            Ready to End
          </button>
        </div>
        <div className="task-list-card">
        <div className="task-list-scroll">
          <Table stickyHeader size="small" className="task-list-table" sx={{ minWidth: 1120 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" className="sticky-sr" sx={{ width: 70 }}>Sr. No.</TableCell>
                <TableCell align="center" className="sticky-action" sx={{ width: 115 }}>Action</TableCell>
                <TableCell className="sticky-equipment" sx={{ width: 145 }}>Equipment No.</TableCell>
                <TableCell sx={{ width: 135 }}>Equipment Group</TableCell>
                <TableCell sx={{ width: 120 }}>Process Code</TableCell>
                <TableCell sx={{ minWidth: 280 }}>Process Name</TableCell>
                <TableCell align="center" sx={{ minWidth: 180 }}>Start ETA</TableCell>
                <TableCell align="center" sx={{ minWidth: 180 }}>End ETA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: Math.min(pageSize, 8) }, (_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: COLUMN_COUNT }, (__, cell) => (
                      <TableCell key={cell} sx={{ py: 1 }}>
                        <Skeleton variant="rounded" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!loading &&
                rows.map((row, index) => (
                  <TableRow
                    key={row.process_id}
                    hover
                    sx={{ "& td": { borderColor: "#eef2f7" } }}
                  >
                    <TableCell align="center" className="sticky-sr">
                      {(page - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell align="center" className="sticky-action">
                      <Button
                        className={`task-list-action-button task-list-action-${activeTaskType}`}
                        size="small"
                        variant={activeTaskType === "end" ? "outlined" : "contained"}
                        color={activeTaskType === "end" ? "error" : "success"}
                        endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                        disabled={submittingProcessId === row.process_id}
                        onClick={() => openExistingActionFlow(row)}
                        sx={{ minWidth: 82, textTransform: "none", fontWeight: 700 }}
                      >
                        {activeTaskType === "end" ? "End" : "Start"}
                      </Button>
                    </TableCell>
                    <TableCell className="sticky-equipment task-list-equipment">
                      {row.equipment_number || "—"}
                    </TableCell>
                    <TableCell className="task-list-group">
                      {(() => {
                        const group = groups.find(
                          (item) => item.equipment_group_code === row.equipment_group,
                        );
                        const value = row.equipment_group || "—";
                        const fullName = group?.equipment_group_name;
                        return fullName && fullName !== value ? (
                          <Tooltip title={fullName}><span>{value}</span></Tooltip>
                        ) : value;
                      })()}
                    </TableCell>
                    <TableCell className="task-list-process-code">
                      {row.process_code || "—"}
                    </TableCell>
                    <TableCell className="task-list-process-name">{row.process_name || "—"}</TableCell>
                    <TableCell align="center">
                      {renderDate(row.start_eta, row.project_tz || projectTimezone)}
                    </TableCell>
                    <TableCell align="center">
                      {renderDate(row.end_eta, row.project_tz || projectTimezone)}
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 7, color: "#64748b" }}>
                    {activeTaskType === "start"
                      ? "No processes are currently ready to start."
                      : "No processes are currently ready to end."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="task-list-pagination">
          <span>Total Records: <strong>{total}</strong></span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>Per Page:</span>
            <Select
              size="small"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </Select>
            <Tooltip title="Previous page">
              <span>
                <IconButton
                  className="task-list-page-button"
                  aria-label="Previous page"
                  disabled={loading || page <= 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  <NavigateBeforeIcon />
                </IconButton>
              </span>
            </Tooltip>
            <span>Page {page} of {totalPages}</span>
            <Tooltip title="Next page">
              <span>
                <IconButton
                  className="task-list-page-button"
                  aria-label="Next page"
                  disabled={loading || total === 0 || page >= totalPages}
                  onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                >
                  <NavigateNextIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={4000} theme="light" />
    </div>
  );
}

export default Activities;
